const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export const CHAT_SHARE_PATH = '/share/ai';
export const CHAT_SHARE_SNAPSHOT_PREFIX = 'v1.';
export const CHAT_SHARE_SNAPSHOT_GZIP_PREFIX = 'v2g.';
/**
 * Snapshot links live in the URL hash, so they are not sent to Vercel.
 * Keep a cap so Slack/email pastes stay usable; gzip usually keeps chats under this.
 */
export const MAX_CHAT_SHARE_URL_LENGTH = 250_000;

export function chatShareTitle(session, messages) {
  const titled = typeof session?.title === 'string' ? session.title.trim() : '';
  if (titled) return titled.slice(0, 200);
  const firstUser = (messages || []).find((message) => (
    message?.role === 'user' && typeof message.content === 'string' && message.content.trim()
  ));
  if (firstUser) return firstUser.content.trim().slice(0, 80);
  return 'PrecisionAI chat';
}

export function buildChatShareSnapshot({ title, messages } = {}) {
  return {
    v: 1,
    title: chatShareTitle({ title }, messages),
    messages: (messages || [])
      .filter((message) => (
        message
        && (message.role === 'user' || message.role === 'assistant')
        && typeof message.content === 'string'
        && message.content
      ))
      .map((message) => ({ role: message.role, content: message.content })),
  };
}

function bytesToBase64Url(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function utf8Bytes(text) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text);
  return Uint8Array.from(unescape(encodeURIComponent(text)), (char) => char.charCodeAt(0));
}

function utf8String(bytes) {
  if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes);
  return decodeURIComponent(escape(String.fromCharCode(...bytes)));
}

export function isSnapshotShareToken(token) {
  return typeof token === 'string' && (
    token.startsWith(CHAT_SHARE_SNAPSHOT_GZIP_PREFIX)
    || token.startsWith(CHAT_SHARE_SNAPSHOT_PREFIX)
  );
}

async function gzipBytes(bytes) {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function gunzipBytes(bytes) {
  if (typeof DecompressionStream === 'undefined') return null;
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function snapshotFromParsed(parsed) {
  if (!parsed || !Array.isArray(parsed.messages)) return null;
  return {
    v: 1,
    title: chatShareTitle(parsed, parsed.messages),
    messages: buildChatShareSnapshot(parsed).messages,
  };
}

export async function encodeChatShareSnapshot(snapshot) {
  const json = JSON.stringify(snapshot);
  const raw = utf8Bytes(json);
  const gzipped = await gzipBytes(raw);
  if (gzipped && gzipped.length < raw.length) {
    return CHAT_SHARE_SNAPSHOT_GZIP_PREFIX + bytesToBase64Url(gzipped);
  }
  return CHAT_SHARE_SNAPSHOT_PREFIX + bytesToBase64Url(raw);
}

export async function decodeChatShareSnapshot(token) {
  if (!isSnapshotShareToken(token)) return null;
  try {
    if (token.startsWith(CHAT_SHARE_SNAPSHOT_GZIP_PREFIX)) {
      const unzipped = await gunzipBytes(base64UrlToBytes(token.slice(CHAT_SHARE_SNAPSHOT_GZIP_PREFIX.length)));
      if (!unzipped) return null;
      return snapshotFromParsed(JSON.parse(utf8String(unzipped)));
    }
    return snapshotFromParsed(JSON.parse(
      utf8String(base64UrlToBytes(token.slice(CHAT_SHARE_SNAPSHOT_PREFIX.length))),
    ));
  } catch {
    return null;
  }
}

export function buildChatShareUrl(token, {
  origin = (typeof window !== 'undefined' ? window.location.origin : ''),
} = {}) {
  if (!token) return '';
  if (isSnapshotShareToken(token)) {
    return `${origin}${CHAT_SHARE_PATH}#${token}`;
  }
  return `${origin}${CHAT_SHARE_PATH}/${encodeURIComponent(token)}`;
}

function shareTokenFromPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const nested = data.share && typeof data.share === 'object' ? data.share : null;
  const token = data.share_token
    || data.shareToken
    || data.public_token
    || data.publicToken
    || data.token
    || nested?.share_token
    || nested?.token
    || nested?.id;
  if (token != null && token !== '') return String(token);
  if (typeof data.url === 'string') {
    const match = data.url.match(/\/share\/ai\/([^/?#]+)/);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  // Share rows often use UUID ids. Numeric session ids are not public tokens.
  if (typeof data.id === 'string' && !/^\d+$/.test(data.id) && data.id.length >= 8) {
    return data.id;
  }
  return null;
}

async function requestShareToken(request, path, options) {
  if (!request) return null;
  try {
    const response = await request(path, options);
    if (!response?.ok) return null;
    return shareTokenFromPayload(await response.json().catch(() => ({})));
  } catch {
    return null;
  }
}

/**
 * Prefer a server share token so the URL stays short and the transcript
 * is not stuffed into the link. Falls back to a snapshot hash permalink.
 */
export async function createChatShare({ sessionId, snapshot, request } = {}) {
  const body = snapshot || buildChatShareSnapshot({});
  if (!body.messages.length) {
    const error = new Error('Nothing to share yet.');
    error.code = 'EMPTY_CHAT';
    throw error;
  }

  if (sessionId) {
    const fromSession = await requestShareToken(
      request,
      `/api/chat/sessions/${sessionId}/share`,
      { method: 'POST' },
    );
    if (fromSession) {
      return { token: fromSession, source: 'server', url: buildChatShareUrl(fromSession) };
    }
  }

  const fromCollection = await requestShareToken(
    request,
    '/api/chat/shares',
    { method: 'POST', body: JSON.stringify(body) },
  );
  if (fromCollection) {
    return { token: fromCollection, source: 'server', url: buildChatShareUrl(fromCollection) };
  }

  const token = await encodeChatShareSnapshot(body);
  const url = buildChatShareUrl(token);
  if (url.length > MAX_CHAT_SHARE_URL_LENGTH) {
    const error = new Error('This chat is too long to share as a link.');
    error.code = 'CHAT_TOO_LONG';
    throw error;
  }
  return { token, source: 'snapshot', url };
}

export async function loadChatShare(token, { requestPublic } = {}) {
  const value = typeof token === 'string' ? decodeURIComponent(token) : '';
  if (!value) {
    throw new Error('Invalid link.');
  }
  if (isSnapshotShareToken(value)) {
    const snapshot = await decodeChatShareSnapshot(value);
    if (!snapshot) throw new Error('Invalid link.');
    return snapshot;
  }

  const path = `/api/chat/shares/${encodeURIComponent(value)}`;
  const response = requestPublic
    ? await requestPublic(path)
    : await fetch(`${BACKEND_URL}${path}`);
  if (!response?.ok) {
    const data = await response?.json?.().catch(() => ({}));
    throw new Error(data?.error || 'This shared chat could not be found.');
  }
  const data = await response.json();
  return {
    title: chatShareTitle(data, data.messages),
    messages: buildChatShareSnapshot(data).messages,
    createdAt: data.created_at || data.createdAt || null,
    live: data.live === true,
  };
}
